/**
 * Dashboard widget configuration.
 *
 * Deliberately kept out of schema.ts: this is plain zod + data with no Drizzle
 * dependency, but it is imported by WidgetManagerContext — an eager provider in
 * App.tsx. Reaching it through schema.ts dragged that module's graph onto the
 * critical path and cost ~134 kB in the entry chunk (measured: 953 kB -> 819 kB
 * when this moved out). Keep client-reachable shared values in leaf modules
 * like this one rather than in schema.ts.
 */
// NOTE: no zod import here, deliberately. This module is reached from
// WidgetManagerProvider, which App.tsx renders eagerly, so anything imported
// here lands on the critical path for every route including /login. The zod
// schema that used to live alongside this data now sits in
// ./widgetConfigSchema, which asserts at compile time that the interface below
// still matches it exactly.
//
// And no import of ./menuGate or ./navigationMenu either, for the same reason:
// the menu keys below are plain strings, and tests/dashboardWidgets.test.ts
// checks every one of them is a real PROJECT_MENU_ITEMS key.

/**
 * The picker's groups, in the order the picker — and a new user's dashboard —
 * shows them (Andrew, 2026-09-30: "sort the widgets logically"). The pulse
 * first; then the three areas a sale moves through, in the order it moves
 * (sold, offered, what is left to sell); then the CRM (leads, their tasks,
 * the people working them); then email; then the running feed.
 */
export const WIDGET_CATEGORY_ORDER = [
  "overview",
  "sales",
  "offers",
  "availability",
  "leads",
  "tasks",
  "team",
  "email-marketing",
  "activity",
] as const;

export type WidgetCategory = (typeof WIDGET_CATEGORY_ORDER)[number];

/** English fallbacks; the picker resolves them with t(titleKey, title). */
export const WIDGET_CATEGORY_LABELS: Record<
  WidgetCategory,
  { title: string; titleKey: string; description: string; descriptionKey: string }
> = {
  overview: { title: "Overview", titleKey: "dashWidgetCategoryOverview", description: "This project's numbers, and what needs someone now", descriptionKey: "dashWidgetCategoryOverviewDescription" },
  sales: { title: "Sales", titleKey: "dashWidgetCategorySales", description: "Sales, deposits, conditions and closings", descriptionKey: "dashWidgetCategorySalesDescription" },
  offers: { title: "Offers", titleKey: "dashWidgetCategoryOffers", description: "Worksheets waiting, converting, and the homes buyers ask for", descriptionKey: "dashWidgetCategoryOffersDescription" },
  availability: { title: "Availability", titleKey: "dashWidgetCategoryAvailability", description: "What is sold, what is left, and what it is worth", descriptionKey: "dashWidgetCategoryAvailabilityDescription" },
  leads: { title: "Leads", titleKey: "dashWidgetCategoryLeads", description: "Registrations, sources and where buyers sit in the journey", descriptionKey: "dashWidgetCategoryLeadsDescription" },
  tasks: { title: "Tasks", titleKey: "dashWidgetCategoryTasks", description: "Follow-ups due and the work that is next", descriptionKey: "dashWidgetCategoryTasksDescription" },
  team: { title: "Team", titleKey: "dashWidgetCategoryTeam", description: "How you and your team are doing", descriptionKey: "dashWidgetCategoryTeamDescription" },
  "email-marketing": { title: "Email Marketing", titleKey: "dashWidgetCategoryEmailMarketing", description: "Campaign performance and automations", descriptionKey: "dashWidgetCategoryEmailMarketingDescription" },
  activity: { title: "Activity", titleKey: "dashWidgetCategoryActivity", description: "Recent interactions, notes and files", descriptionKey: "dashWidgetCategoryActivityDescription" },
};

/**
 * A project menu item a widget belongs to. The same keys the sidebar gates on
 * (shared/menuGate.ts `MENU`).
 */
export type WidgetMenuKey = "contacts" | "tasks" | "emailMarketing" | "offers" | "salesList" | "availability";

export interface WidgetConfig {
  id: string;
  title: string;
  category: WidgetCategory;
  enabled: boolean;
  position?: {
    row: number;
    col: number;
    width: number;
    height: number;
  };
  configuration?: Record<string, any>;
}

export interface WidgetDefinition extends Omit<WidgetConfig, "enabled" | "position"> {
  /**
   * The project menu items this widget belongs to. EVERY one must be open
   * (all four sidebar gates, for this reader, on this project) or the widget
   * is not drawn, not offered in the picker, and its endpoint answers 404
   * (Andrew, 2026-09-30: "If not activated, then don't show any of the widgets
   * or even mention them"). `[]` = always shown; such a widget gates its own
   * rows or columns, and so does the endpoint behind it.
   */
  requires: readonly WidgetMenuKey[];
  /** Translation key for `title`; render with t(def.titleKey, def.title). */
  titleKey: string;
  /** On for a new user and after Reset to Defaults. */
  defaultEnabled: boolean;
  /**
   * The widget is a list of Legacy sync errors, so it is drawn and offered for
   * SaleFish Admins only (shared/legacySyncErrors.ts), and its endpoint answers
   * nobody else's rows.
   */
  salefishAdminsOnly?: true;
}

/**
 * Every widget, IN ORDER within its category — summary first, detail last.
 * The picker and the default layout both read this order.
 */
const DEFINITIONS: readonly WidgetDefinition[] = [
  // ── Overview ── one endpoint (/metrics/pulse); rows and figures gate one by one
  /* The default set is SIX (Andrew, 2026-11-03: the Dashboard was ~19 phone
     screens long and "way too busy"). Every widget below that is off by
     default is either a slice of Needs attention (offers awaiting approval,
     conditions expiring, deposits due, worksheets not sent, follow-ups,
     at-risk leads) or a second view of a figure shown elsewhere; each is still
     one switch away in Configure Widgets. */
  { id: "project-numbers", title: "Key Numbers", titleKey: "dashWidgetProjectNumbersTitle", category: "overview", requires: [], defaultEnabled: true },
  { id: "needs-attention", title: "To Do Today", titleKey: "dashWidgetToDoTodayTitle", category: "overview", requires: [], defaultEnabled: true },

  // ── Sales (Sales List) ──
  { id: "sales-velocity", title: "Sales Velocity", titleKey: "dashWidgetSalesVelocityTitle", category: "sales", requires: ["salesList"], defaultEnabled: true },
  { id: "recent-sales", title: "Recent Sales", titleKey: "dashWidgetRecentSalesTitle", category: "sales", requires: ["salesList"], defaultEnabled: true },
  { id: "conditions-expiring", title: "Conditions Expiring", titleKey: "dashWidgetConditionsExpiringTitle", category: "sales", requires: ["salesList"], defaultEnabled: false },
  { id: "deposits-due", title: "Deposits Due", titleKey: "dashWidgetDepositsDueTitle", category: "sales", requires: ["salesList"], defaultEnabled: false },
  { id: "upcoming-closings", title: "Upcoming Closings", titleKey: "dashWidgetUpcomingClosingsTitle", category: "sales", requires: ["salesList"], defaultEnabled: false },
  { id: "buyer-mix", title: "Buyer Mix", titleKey: "dashWidgetBuyerMixTitle", category: "sales", requires: ["salesList"], defaultEnabled: false },
  { id: "options-upgrades", title: "Options & Upgrades", titleKey: "dashWidgetOptionsUpgradesTitle", category: "sales", requires: ["salesList"], defaultEnabled: false },

  // ── Offers ──
  { id: "worksheet-funnel", title: "Worksheet Funnel", titleKey: "dashWidgetWorksheetFunnelTitle", category: "offers", requires: ["offers"], defaultEnabled: false },
  { id: "offers-awaiting-approval", title: "Awaiting Approval", titleKey: "dashWidgetOffersAwaitingApprovalTitle", category: "offers", requires: ["offers"], defaultEnabled: false },
  { id: "worksheets-not-sent", title: "Worksheets Not Sent", titleKey: "dashWidgetWorksheetsNotSentTitle", category: "offers", requires: ["offers"], defaultEnabled: false, salefishAdminsOnly: true },
  { id: "most-wanted-homes", title: "Most-Wanted Homes", titleKey: "dashWidgetMostWantedHomesTitle", category: "offers", requires: ["offers"], defaultEnabled: false },

  // ── Availability ──
  { id: "sell-through", title: "Sell-through", titleKey: "dashWidgetSellThroughTitle", category: "availability", requires: ["availability"], defaultEnabled: true },
  { id: "inventory-by-status", title: "Inventory by Status", titleKey: "dashWidgetInventoryByStatusTitle", category: "availability", requires: ["availability"], defaultEnabled: false },
  { id: "days-on-market", title: "Days on Market", titleKey: "dashWidgetDaysOnMarketTitle", category: "availability", requires: ["availability"], defaultEnabled: false },
  { id: "price-changes", title: "Price Changes", titleKey: "dashWidgetPriceChangesTitle", category: "availability", requires: ["availability"], defaultEnabled: false },
  { id: "recently-released", title: "Recently Released", titleKey: "dashWidgetRecentlyReleasedTitle", category: "availability", requires: ["availability"], defaultEnabled: false },

  // ── Leads (Contacts) ──
  { id: "new-leads-counter", title: "New Leads Today", titleKey: "dashWidgetNewLeadsCounterTitle", category: "leads", requires: ["contacts"], defaultEnabled: false },
  { id: "registrations-trend", title: "Registrations Trend", titleKey: "dashWidgetRegistrationsTrendTitle", category: "leads", requires: ["contacts"], defaultEnabled: false },
  { id: "buyer-journey-distribution", title: "Buyer Journey", titleKey: "dashWidgetBuyerJourneyDistributionTitle", category: "leads", requires: ["contacts"], defaultEnabled: false },
  { id: "lead-source-breakdown", title: "How Leads Find You", titleKey: "dashWidgetLeadSourceBreakdownTitle", category: "leads", requires: ["contacts"], defaultEnabled: false },
  // Cross-area: a lead's source (Contacts) against whether they bought (Sales List).
  { id: "lead-source-to-sale", title: "Lead Source to Sale", titleKey: "dashWidgetLeadSourceToSaleTitle", category: "leads", requires: ["contacts", "salesList"], defaultEnabled: false },
  { id: "lead-classifications", title: "Lead Temperature", titleKey: "dashWidgetLeadClassificationsTitle", category: "leads", requires: ["contacts"], defaultEnabled: false },
  // Titled "Needs Attention" until the pulse widget took that name (2026-09-28);
  // the id is unchanged, so saved layouts keep it.
  { id: "at-risk-leads", title: "At-Risk Leads", titleKey: "dashWidgetAtRiskLeadsTitle", category: "leads", requires: ["contacts"], defaultEnabled: false },
  // Largely restates new-leads-counter.
  { id: "new-registrations", title: "New Registrations", titleKey: "dashWidgetNewRegistrationsTitle", category: "leads", requires: ["contacts"], defaultEnabled: false },

  // ── Tasks ──
  // Overdue TASKS on contacts: both areas, or it names one the reader cannot open.
  { id: "overdue-followups", title: "Needs Follow-Up", titleKey: "dashWidgetOverdueFollowupsTitle", category: "tasks", requires: ["contacts", "tasks"], defaultEnabled: false },
  { id: "recent-tasks", title: "Recent Tasks", titleKey: "dashWidgetRecentTasksTitle", category: "tasks", requires: ["tasks"], defaultEnabled: false },
  { id: "task-classifications", title: "Task Pulse", titleKey: "dashWidgetTaskClassificationsTitle", category: "tasks", requires: ["tasks"], defaultEnabled: false },

  // ── Team ──
  { id: "personal-kpi-panel", title: "My Performance", titleKey: "dashWidgetPersonalKpiPanelTitle", category: "team", requires: ["tasks"], defaultEnabled: false },
  // A manager's view; its columns gate one by one (server and client).
  { id: "team-kpi-panel", title: "Team Activity", titleKey: "dashWidgetTeamKpiPanelTitle", category: "team", requires: [], defaultEnabled: false },
  { id: "rep-leaderboard", title: "Rep Leaderboard", titleKey: "dashWidgetRepLeaderboardTitle", category: "team", requires: ["salesList"], defaultEnabled: false },

  // ── Email Marketing ── only meaningful once ActiveCampaign is connected
  { id: "email-performance", title: "Email Performance", titleKey: "dashWidgetEmailPerformanceTitle", category: "email-marketing", requires: ["emailMarketing"], defaultEnabled: false },
  { id: "recent-campaigns", title: "Recent Campaigns", titleKey: "dashWidgetRecentCampaignsTitle", category: "email-marketing", requires: ["emailMarketing"], defaultEnabled: false },
  { id: "automation-status", title: "Active Automations", titleKey: "dashWidgetAutomationStatusTitle", category: "email-marketing", requires: ["emailMarketing"], defaultEnabled: false },

  // ── Activity ──
  // Every row is a touchpoint on a contact — a Contacts widget, whatever its group.
  { id: "recent-activity-feed", title: "Recent Activity", titleKey: "dashWidgetRecentActivityFeedTitle", category: "activity", requires: ["contacts"], defaultEnabled: true },
  { id: "recent-notes", title: "Recent Notes", titleKey: "dashWidgetRecentNotesTitle", category: "activity", requires: ["contacts"], defaultEnabled: false },
  { id: "recent-files", title: "Recent Files", titleKey: "dashWidgetRecentFilesTitle", category: "activity", requires: ["contacts"], defaultEnabled: false },
];

const CATEGORY_RANK = new Map<string, number>(WIDGET_CATEGORY_ORDER.map((c, i) => [c, i]));

/** Every widget id, in picker order: by category, then summary to detail. */
export const WIDGET_ORDER: readonly string[] = DEFINITIONS
  .map((d, i) => ({ d, i }))
  .sort((a, b) => (CATEGORY_RANK.get(a.d.category)! - CATEGORY_RANK.get(b.d.category)!) || a.i - b.i)
  .map(({ d }) => d.id);

export const WIDGET_DEFINITIONS: Record<string, WidgetDefinition> = Object.fromEntries(
  WIDGET_ORDER.map((id) => [id, DEFINITIONS.find((d) => d.id === id)!]),
);

/**
 * Is this widget's area open for this reader? `open` is the set of open
 * project menu keys (useProjectMenuGate / openProjectMenuKeys). An unknown id
 * is never open — a saved layout can name a widget that no longer exists.
 */
/**
 * The Dashboard's tabs on a phone (Andrew, 2026-11-03: "trim + tabs"). One
 * column of every widget ran ~19 screens; on a phone the widgets are grouped
 * under word tabs by their category, and a tab shows only when it holds a
 * widget. Every category belongs to exactly one tab (tests/dashboardWidgets).
 */
export const DASHBOARD_PHONE_TABS = [
  { id: "today", label: "Today", labelKey: "dashTabToday", categories: ["overview", "activity"] },
  { id: "sales", label: "Sales", labelKey: "dashTabSales", categories: ["sales", "offers", "availability"] },
  { id: "leads", label: "Leads", labelKey: "dashTabLeads", categories: ["leads", "email-marketing"] },
  { id: "team", label: "Team", labelKey: "dashTabTeam", categories: ["tasks", "team"] },
] as const satisfies readonly { id: string; label: string; labelKey: string; categories: readonly WidgetCategory[] }[];

export type DashboardPhoneTab = (typeof DASHBOARD_PHONE_TABS)[number]["id"];

/** The phone tab a widget sits under. */
export function dashboardPhoneTabOf(widgetId: string): DashboardPhoneTab {
  const category = WIDGET_DEFINITIONS[widgetId]?.category;
  return DASHBOARD_PHONE_TABS.find((tab) => (tab.categories as readonly string[]).includes(category ?? ""))?.id ?? "today";
}

export function isWidgetOpen(id: string, open: ReadonlySet<string>, seesSyncErrors: boolean): boolean {
  const def = WIDGET_DEFINITIONS[id];
  return !!def && (!def.salefishAdminsOnly || seesSyncErrors) && def.requires.every((key) => open.has(key));
}

export interface WidgetPickerGroup {
  category: WidgetCategory;
  title: string;
  titleKey: string;
  description: string;
  descriptionKey: string;
  ids: string[];
}

/**
 * What the Customize panel lists: only widgets whose areas are open, grouped
 * in WIDGET_CATEGORY_ORDER, and no group at all for a category with nothing
 * open in it — a heading for "Sales" on a project without a Sales List would
 * mention the thing the rule says must not be mentioned.
 */
export function widgetPickerGroups(open: ReadonlySet<string>, seesSyncErrors: boolean): WidgetPickerGroup[] {
  return WIDGET_CATEGORY_ORDER.flatMap((category) => {
    const ids = WIDGET_ORDER.filter((id) => WIDGET_DEFINITIONS[id].category === category && isWidgetOpen(id, open, seesSyncErrors));
    if (ids.length === 0) return [];
    return [{ category, ...WIDGET_CATEGORY_LABELS[category], ids }];
  });
}
